'use client';

import { useEffect, useRef, useState } from 'react';

export type KeyedAnimationValue = string | number | null | undefined;

export interface UseKeyedAnimationOptions {
  /**
   * Value that triggers an animation when it changes.
   * Common usage: step number, tab id, route slug, etc.
   */
  keyValue: KeyedAnimationValue;

  /**
   * Whether the animation system is enabled.
   * If disabled, the hook always returns `false`.
   */
  enabled?: boolean;

  /**
   * How long to keep the "animate" flag true.
   * This should generally be >= the CSS animation duration you apply via className.
   */
  durationMs?: number;

  /**
   * If true, also triggers the animation once on initial mount.
   */
  animateOnMount?: boolean;
}

/**
 * useKeyedAnimation
 * Returns a boolean flag that becomes `true` briefly whenever `keyValue` changes.
 *
 * Pattern:
 * 1. First effect: set `isAnimating` to false (ensures class is removed and animations can restart)
 * 2. Next animation frame: set it to true (re-add class, triggers CSS animation)
 * 3. After `durationMs`: set it back to false (clean up for next animation)
 *
 * This approach ensures that:
 * - CSS animations can re-trigger even when moving from step 1 → 2 → 3
 * - Multiple rapid changes don't queue up animation frames
 * - Motion preferences are respected if defined in the animation className
 */
export function useKeyedAnimation({
  keyValue,
  enabled = true,
  durationMs = 350,
  animateOnMount = false,
}: UseKeyedAnimationOptions): boolean {
  const [isAnimating, setIsAnimating] = useState(false);

  const prevKeyRef = useRef<KeyedAnimationValue>(undefined);
  const mountedRef = useRef(false);

  useEffect(() => {
    if (!enabled) {
      setIsAnimating(false);
      return;
    }

    const isFirstMount = !mountedRef.current;

    if (isFirstMount) {
      mountedRef.current = true;
      prevKeyRef.current = keyValue;

      if (!animateOnMount) return;
      // fallthrough to trigger animation on mount
    } else {
      // Only animate on changes
      if (Object.is(prevKeyRef.current, keyValue)) return;
      prevKeyRef.current = keyValue;
    }

    // Always reset animation state first to allow re-triggering
    setIsAnimating(false);

    const raf = globalThis.requestAnimationFrame(() => {
      setIsAnimating(true);
    });

    const timeout = globalThis.setTimeout(() => {
      setIsAnimating(false);
    }, Math.max(0, durationMs));

    return () => {
      globalThis.cancelAnimationFrame(raf);
      globalThis.clearTimeout(timeout);
    };
  }, [animateOnMount, durationMs, enabled, keyValue]);

  return isAnimating;
}
